// 共享列表组件 — 自定义 flex 行布局 + 分页
// 替代 antd Table / fluent-kit FluentList,按组件映射表用 makeStyles 做行布局
import { useMemo, useState, type ReactNode } from 'react';
import {
  makeStyles,
  tokens,
  mergeClasses,
  Button,
  Spinner,
} from '@fluentui/react-components';
import { ChevronLeftRegular, ChevronRightRegular } from '@fluentui/react-icons';

export interface SimpleListItem {
  key: string;
  media?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  extra?: ReactNode;
  onClick?: () => void;
}

interface SimpleListProps {
  items: SimpleListItem[];
  loading?: boolean;
  emptyText?: string;
  pageSize?: number;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    overflow: 'hidden',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    cursor: 'pointer',
    minHeight: '56px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    ':last-child': { borderBottom: 'none' },
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  rowNoClick: {
    cursor: 'default',
    ':hover': { backgroundColor: 'transparent' },
  },
  media: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
  },
  main: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  title: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  description: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  extra: {
    flexShrink: 0,
  },
  empty: {
    padding: '48px 16px',
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
  },
  loadingWrap: {
    padding: '48px 16px',
    display: 'flex',
    justifyContent: 'center',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '8px',
    padding: '8px 16px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  pageInfo: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginRight: '8px',
  },
});

export function SimpleList({
  items,
  loading,
  emptyText = '暂无数据',
  pageSize = 20,
}: SimpleListProps) {
  const styles = useStyles();
  const [page, setPage] = useState(0);

  // pageSize 非法(Infinity/NaN/≤0)时回退为"全量显示不分页"。
  // 否则 currentPage * pageSize = 0 * Infinity = NaN,slice(NaN,NaN) 得到空数组,列表会渲染为空。
  const effectivePageSize =
    Number.isFinite(pageSize) && pageSize > 0 ? pageSize : items.length || 1;

  const totalPages = Math.max(1, Math.ceil(items.length / effectivePageSize));
  const currentPage = Math.min(page, totalPages - 1);

  const paged = useMemo(
    () =>
      items.slice(
        currentPage * effectivePageSize,
        currentPage * effectivePageSize + effectivePageSize,
      ),
    [items, currentPage, effectivePageSize],
  );

  if (loading) {
    return (
      <div className={styles.root}>
        <div className={styles.loadingWrap}>
          <Spinner size="large" label="加载中" />
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className={styles.root}>
        <div className={styles.empty}>{emptyText}</div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {paged.map((item) => (
        <div
          key={item.key}
          className={mergeClasses(styles.row, !item.onClick && styles.rowNoClick)}
          onClick={item.onClick}
        >
          {item.media && <div className={styles.media}>{item.media}</div>}
          <div className={styles.main}>
            {item.title && <div className={styles.title}>{item.title}</div>}
            {item.description && (
              <div className={styles.description}>{item.description}</div>
            )}
          </div>
          {item.extra && <div className={styles.extra}>{item.extra}</div>}
        </div>
      ))}
      {totalPages > 1 && (
        <div className={styles.footer}>
          <span className={styles.pageInfo}>
            第 {currentPage + 1} / {totalPages} 页 · 共 {items.length} 条
          </span>
          <Button
            size="small"
            appearance="subtle"
            icon={<ChevronLeftRegular />}
            disabled={currentPage === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          />
          <Button
            size="small"
            appearance="subtle"
            icon={<ChevronRightRegular />}
            disabled={currentPage >= totalPages - 1}
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
          />
        </div>
      )}
    </div>
  );
}
